import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GenericModalService, ModalMultiButton, ModalSettingsConfirmMultiButton } from 'src/app/shared/generic-modal/generic-modal.service';
import { BizDataGridDirective } from 'src/app/shared/grid/biz-grid.directive';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { DataFieldGroup } from '../models/data-field-group';
import { DeveloperService } from '../services/developer.service';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective as BizDataGridDirective_1 } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule } from 'devextreme-angular/ui/nested';

@Component({
    selector: 'app-developer-data-field-groups',
    templateUrl: './developer-data-field-groups.component.html',
    styleUrls: ['./developer-data-field-groups.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, BizDataGridDirective_1, DxoEditingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule]
})
export class DeveloperDataFieldGroupsComponent implements OnInit, AfterViewInit {
    @ViewChild(BizDataGridDirective, { static: true }) dataGrid: BizDataGridDirective;

    dataFieldGroups: DataFieldGroup[] = [];
    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;

    constructor(
        private developerService: DeveloperService,
        private toastService: ToastService,
        private genericModalService: GenericModalService,
        public modal: NgbActiveModal,
        private currentUserService: CurrentUserService) { }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_DisplayDataFieldGroup_Create');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_DisplayDataFieldGroup_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_DisplayDataFieldGroup_Delete');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.dataFieldGroups = [];
            this.dataFieldGroups = await this.developerService.getDataFieldGroups();
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
    }

    onInitNewRow(e: any): void {
        e.data = {
            DataFieldGroupId: -1,
            DisplayName: '',
            Description: '',
            SortOrder: 0,
        } as DataFieldGroup;
    }

    onRowInserted(e: any): void {
        const group = e.data as DataFieldGroup;
        this.saveGroup(group);
    }

    onRowUpdated(e: any): void {
        const group = e.data as DataFieldGroup;
        this.saveGroup(group);
    }

    async onRowRemoving(e: any): Promise<void> {
        const group = e.data as DataFieldGroup;

        const deferred = $.Deferred();
        e.cancel = deferred.promise();

        try {
            await this.developerService.deleteDataFieldGroup(group.DataFieldGroupId, err => err?.status !== 409);
            this.toastService.showToast('Group deleted...', ToastType.success);
            deferred.resolve(false);
        }
        catch (err) {
            // Process 409 error here; other errors are processed using generic error handling
            if (err?.status === 409) {
                const settings = new ModalSettingsConfirmMultiButton('Delete failed', `Error details: <pre style='white-space: pre-wrap;'>${err.error}</pre>`);
                settings.size = 'lg';
                settings.backdrop = 'static';
                settings.buttons = [new ModalMultiButton('Close', null, 'btn btn-primary')];
                await this.genericModalService.confirmMultiButton(settings);
            }
            deferred.resolve(true);
        }
    }

    async saveGroup(group: DataFieldGroup): Promise<DataFieldGroup> {
        try {
            const newGroup = await this.developerService.saveDataFieldGroup(group);
            group.DataFieldGroupId = newGroup?.DataFieldGroupId ?? group.DataFieldGroupId;
            this.toastService.showToast('Group saved...', ToastType.success);
            return newGroup;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save group...', ToastType.error);
            return undefined;
        }
    }

    ok(): void {
        this.modal.close(this.dataFieldGroups);
    }

    cancel(): void {
        this.modal.dismiss();
    }
}
